import { useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { Table, Timeline, Tag, Button, Modal, Input, Alert, Skeleton, Result, Popconfirm, message, EntityAvatar, CodeChip, entityHue, cn } from '../../ui';
import type { TagTone } from '../../ui';
import { FileText, AlertTriangle, Users, Package, PackageCheck, Receipt, History, Clock } from 'lucide-react';
import { orderStatusLabel } from '../../lib/statusTone';
import { request } from '../../api/client';
import { usePermission, useAuthStore } from '../../store/auth';
import { formatIst } from '../../lib/time';
import { fmtQty } from '../../lib/format';
import { boxLoosePhrase } from '../../lib/packaging';
import { ReceiveReturnModal } from './ReceiveReturnModal';
import { CreditReturnModal } from './CreditReturnModal';
import { CreditNoteModal } from '../../components/CreditNoteModal';
import { Pill } from '../../components/TwDataTable';
import { OrderLink, RetailerLink, UserLink, ProductLink } from '../../components/EntityLinks';
import { RoleBadge, roleLabel } from '../../components/RoleBadge';
import { AttachmentCell } from '../../components/AttachmentPreview';
import { CopyValue } from '../../components/CopyValue';
import { toImageUrls, productImages } from '../../lib/imageUrls';
import { ProductThumb } from '../../components/ProductThumb';
import { accentForProductName } from '../../lib/productAccent';
import { inr2 } from '../../lib/format';

const CONDITION_COLOR: Record<string, string> = {
  RESALABLE: 'green', NEAR_EXPIRY: 'lime', DAMAGED: 'orange', DEFECTIVE: 'gold', MISMATCH: 'gold', EXPIRED: 'red', BLOCKED: 'volcano', MIXED: 'purple',
};

const STATUS_COLOR: Record<string, string> = {
  REQUESTED: 'blue', APPROVED: 'green', IN_TRANSIT: 'gold', RECEIVED: 'purple',
  CREDITED: 'green', REJECTED: 'red', CANCELLED: 'default', POSTED: 'green',
};
/**
 * `POSTED` is the legacy direct-post return's end state — goods back in and the credit note raised in
 * one step, which is what CREDITED means. The register counts and filters the two as one bucket, so
 * the record itself must not be the one place that calls it something else.
 */
const STATUS_ALIAS: Record<string, string> = { POSTED: 'CREDITED' };
const label = (s?: string) => String(STATUS_ALIAS[s ?? ''] ?? s ?? '').replace(/_/g, ' ');

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "2026-07-06" is a machine's way of writing a date; every other register writes "06 Jul 2026". */
const fmtDate = (v: any) => {
  const d = v ? String(v).substring(0, 10) : null;
  if (!d) return null;
  const [y, m, dd] = d.split('-');
  return MONTHS[Number(m) - 1] ? `${dd} ${MONTHS[Number(m) - 1]} ${y}` : d;
};

/**
 * WHY THE GOODS CAME BACK — its wording, its colour, and whose fault the system treats it as.
 *
 * The panel printed the raw code title-cased ("Damaged In Transit"): neither the wording the retailer
 * actually picked it by, nor the fact that decides what happens next. Fault is that fact — a
 * seller/carrier-fault return is normally credited in full, a buyer-choice one is credited on the
 * condition the goods come back in. It drives the money, so it is said here instead of being tribal
 * knowledge held by whoever runs the Accounts screen.
 *
 * Legacy returns stored free text rather than a code; those fall through to a neutral tag with no
 * fault claimed, because we genuinely do not know which it was.
 */
export const REASON_META: Record<string, { label: string; tone: TagTone; fault: 'seller' | 'buyer' }> = {
  DAMAGED_IN_TRANSIT:  { label: 'Damaged in transit',         tone: 'red',     fault: 'seller' },
  DEFECTIVE:           { label: 'Defective / quality issue',  tone: 'red',     fault: 'seller' },
  WRONG_ITEM:          { label: 'Wrong item / short-shipped', tone: 'gold',    fault: 'seller' },
  EXPIRED_ON_DELIVERY: { label: 'Expired on delivery',        tone: 'red',     fault: 'seller' },
  NOT_NEEDED:          { label: 'Not needed / over-ordered',  tone: 'blue',    fault: 'buyer' },
  ORDERED_BY_MISTAKE:  { label: 'Ordered by mistake',         tone: 'blue',    fault: 'buyer' },
  CHANGED_MIND:        { label: 'Changed mind',               tone: 'blue',    fault: 'buyer' },
  OTHER:               { label: 'Other',                      tone: 'neutral', fault: 'buyer' },
};
const FAULT_LINE: Record<string, string> = {
  seller: 'Seller / carrier fault — normally credited in full.',
  buyer: 'Buyer’s choice — the credit follows the condition the goods come back in.',
};

/** One labelled fact. SECTION / label / value stay three visible levels. */
function OField({ label: lbl, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('min-w-0', className)}>
      <div className="type-label text-muted">{lbl}</div>
      <div className="mt-0.5 min-w-0 type-body text-content">{children}</div>
    </div>
  );
}

/** A titled block. `flex` (not inline-flex) on the title: an inline one does not end the line. */
function Section({ title, icon, children, className }: {
  title: React.ReactNode; icon?: React.ReactNode; children: React.ReactNode; className?: string;
}) {
  return (
    <div className={cn('rounded-card border border-line bg-surface p-3 shadow-e1', className)}>
      <div className="mb-2 flex items-center gap-1.5 type-overline text-muted">
        {icon && <span className="text-brand-600 dark:text-brand-400">{icon}</span>}
        {title}
      </div>
      {children}
    </div>
  );
}

/** The tint each act in the lifecycle wears, so the strip reads as a track rather than four boxes. */
const ACT_TINT: Record<string, string> = {
  raised:   'border-sky-500/25 bg-sky-500/[0.06]',
  // UNSETTLED: amber, because nothing here is final — an ask the money has not moved on, or a
  // decision that was taken back and has to be made again.
  requested: 'border-warning-500/35 bg-warning-500/[0.09]',
  approved: 'border-brand-500/25 bg-brand-500/[0.06]',
  received: 'border-violet-500/25 bg-violet-500/[0.06]',
  credited: 'border-brand-500/30 bg-brand-500/[0.08]',
  reversed: 'border-danger-500/30 bg-danger-500/[0.06]',
};

/** The decisions this panel's "Approved by" slot can hold, and how each one reads in a sentence. */
const DECISION_VERB: Record<string, string> = { APPROVED: 'Approved', REJECTED: 'Rejected' };

/**
 * A DECISION THAT NO LONGER STANDS.
 *
 * A Super Admin send-back deliberately nulls returns.approved_by / approved_at — the approval HAS been
 * withdrawn, and a return that is once again waiting for a decision must not name an approver as though
 * one were in force. But clearing the column also cleared the fact out of this panel, so a return two
 * people had already decided on read exactly like one nobody had ever opened; the only way to find out
 * otherwise was to scroll to the audit trail at the foot of the page and reconstruct it.
 *
 * So the card keeps saying what is true NOW and names the superseded decision underneath it, out of
 * return_status_history, which still holds every transition.
 *
 * The LAST undone decision, not the first: this return was approved, sent back, rejected, then sent back
 * again. Reporting the approval there would be a different kind of wrong answer — true once, but not the
 * thing that last happened to this record.
 */
function supersededDecision(history: any[]): { verb: string; decision: any; undo: any } | null {
  const h = history ?? [];
  for (let i = h.length - 1; i >= 0; i--) {
    const verb = DECISION_VERB[h[i].toStatus];
    if (!verb) continue;
    // Undone only if something AFTER it put the return back in the queue. A decision that still stands
    // is not this function's business — the record's own columns carry it.
    const undo = h.slice(i + 1).find((x: any) => x.fromStatus === h[i].toStatus && x.toStatus === 'REQUESTED');
    return undo ? { verb, decision: h[i], undo } : null;
  }
  return null;
}

/**
 * A NAMED ACT: who did it, in what role, and when.
 *
 * Four names in a row ("Demo Retailer", "Super Admin", "Priya Accounts") tell a reader nothing about
 * the authority behind each move, which is the whole point of an approval trail — so every actor
 * carries their role as a coloured pill beside the name. A stage nobody has reached yet says who it
 * is waiting on instead of rendering an empty box.
 */
function ActorCard({ act, label: lbl, name, role, userId, at, awaiting, note }: {
  act: keyof typeof ACT_TINT; label: string;
  name?: string | null; role?: string | null; userId?: string | null; at?: string | null;
  awaiting?: string; note?: ReactNode;
}) {
  if (!name) {
    if (!awaiting) return null;
    return (
      <div className="rounded-control border border-dashed border-line bg-surface-2/60 p-2.5">
        <div className="type-label text-muted">{lbl}</div>
        <div className="mt-1 type-body text-subtle">awaiting {awaiting}</div>
      </div>
    );
  }
  return (
    <div className={cn('rounded-control border p-2.5', ACT_TINT[act])}>
      <div className="type-label text-muted">{lbl}</div>
      <div className="mt-1 flex min-w-0 items-start gap-2">
        <EntityAvatar name={name} size={28} />
        <div className="min-w-0">
          <div className="min-w-0 type-body-strong text-content">
            <UserLink id={userId} role={role}>{name}</UserLink>
          </div>
          {/* A pill that only repeats the name says nothing twice — the seeded "Super Admin" user. */}
          {roleLabel(role ?? '') !== name && <RoleBadge role={role} className="mt-0.5" />}
          {at && <div className="mt-0.5 type-caption text-subtle">{formatIst(at)}</div>}
          {note && <div className="mt-1 break-words type-caption text-muted">{note}</div>}
        </div>
      </div>
    </div>
  );
}

/**
 * The return detail — header + lines + audit trail + approve/reject/withdraw actions. Self-contained (fetches
 * by id) and always shown inside the Returns list's right-side Drawer: both an in-app row click and a
 * deep-link (/returns/:id from the notification bell / activity-log "Open record") open this same drawer, so
 * there is one consistent presentation. `onClosed` lets the drawer close itself after a terminal action
 * (reject/withdraw remove the return from the actor's queue). Also embedded in the Returned-stock page.
 */
export function ReturnDetailPanel({ id, onClosed }: { id: string; onClosed?: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const canApprove = usePermission('RETURN.APPROVE');
  const canReceive = usePermission('RETURN.RECEIVE');
  const canCredit = usePermission('CREDIT_NOTE.CREATE');
  const canReverse = usePermission('RETURN.CREDIT.REVERSE');
  // RETURN.CREDIT.REVERSE means "may ASK"; this one means "may DECIDE". Gating on the permission
  // rather than a role name keeps a multi-role user judged on what they may actually do.
  const canDecideReversal = usePermission('CREDIT_NOTE.REVERSE.APPROVE');
  const userId = useAuthStore((s) => s.user?.id);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [callOffOpen, setCallOffOpen] = useState(false);
  const [callOffReason, setCallOffReason] = useState('');
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [creditOpen, setCreditOpen] = useState(false);
  const [reverseOpen, setReverseOpen] = useState(false);
  const [reverseReason, setReverseReason] = useState('');
  const [noteDrawerId, setNoteDrawerId] = useState<string | null>(null);

  const { data: r, isLoading, isError } = useQuery({
    queryKey: ['return', id],
    queryFn: () => request<any>({ url: `/returns/${id}` }),
    enabled: !!id,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['return', id] });
    qc.invalidateQueries({ queryKey: ['returns'] });
    qc.invalidateQueries({ queryKey: ['badges'] });
  };
  const approve = useConfirmedMutation({ title: "Approve this return?", content: "The goods can then be received back into the warehouse.", okText: "Yes, approve" }, {
    mutationFn: () => request({ url: `/returns/${id}/approve`, method: 'POST' }),
    onSuccess: () => { message.success('Return approved'); invalidate(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Approve failed'),
  });
  const reject = useConfirmedMutation({ title: "Reject this return?", content: "Nothing comes back into stock and your reason goes to whoever raised it.", okText: "Yes, reject", danger: true }, {
    mutationFn: (reason: string) => request({ url: `/returns/${id}/reject`, method: 'POST', data: { reason } }),
    onSuccess: () => { message.success('Return rejected'); setRejectOpen(false); setRejectReason(''); invalidate(); onClosed?.(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Reject failed'),
  });
  const withdraw = useConfirmedMutation({ title: "Withdraw this return?", content: "It is pulled out of the returns queue.", okText: "Yes, withdraw", danger: true }, {
    mutationFn: () => request({ url: `/returns/${id}/cancel`, method: 'POST' }),
    onSuccess: () => { message.success('Return withdrawn'); invalidate(); onClosed?.(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Withdraw failed'),
  });
  /*
   * An approver closes a return that is never going to complete — almost always because the goods were never
   * actually sent back. Without it an APPROVED / IN_TRANSIT return had no exit at all (receiving is the only
   * way out of IN_TRANSIT, and it will not record a receipt of nothing), so it sat in the queue forever AND
   * kept its units counted against what the retailer may return — locking goods they could never send back.
   */
  const callOff = useConfirmedMutation({
    title: 'Call off this return?',
    content: 'Nothing comes back into stock and no credit note is raised. The units are released so they can be returned again later.',
    okText: 'Yes, call it off', danger: true,
  }, {
    mutationFn: (reason: string) => request({ url: `/returns/${id}/cancel`, method: 'POST', data: { reason } }),
    onSuccess: () => { message.success('Return called off'); setCallOffOpen(false); setCallOffReason(''); invalidate(); onClosed?.(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to call off the return'),
  });
  // WHM: goods are on the way back → in transit; then Receive (a separate modal) records what arrived.
  const markInTransit = useMutation({
    mutationFn: () => request({ url: `/returns/${id}/in-transit`, method: 'POST' }),
    onSuccess: () => { message.success('Marked in transit — ready to receive'); invalidate(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to mark in transit'),
  });
  // Accounts: void a credit note issued in error → the return goes back to RECEIVED, re-creditable. Also
  // refreshes the retailer statement (the compensating debit removed the advance-in-credit).
  /*
   * ONE ENDPOINT, TWO OUTCOMES. An accountant's POST raises a request; a Super Admin's performs the
   * reversal. The confirm text and the toast both have to say which of the two actually happened —
   * telling someone "Credit note reversed" when it is sitting in a queue is the worst kind of lie a
   * money screen can tell.
   */
  const reverse = useConfirmedMutation(
    canDecideReversal
      ? { title: "Reverse the credit on this return?", content: "A compensating debit note is posted against the retailer’s ledger.", okText: "Yes, reverse", danger: true }
      : { title: "Ask the Super Admin to reverse this credit note?", content: "Nothing changes until they decide — the retailer keeps the credit meanwhile.", okText: "Yes, send the request" },
    {
      mutationFn: (reason: string) => request<any>({ url: `/returns/${id}/reverse-credit`, method: 'POST', data: { reason } }),
      onSuccess: (res: any) => {
        message.success(res?.requested
          ? 'Sent to the Super Admin — the credit note stands until they decide'
          : 'Credit note reversed');
        setReverseOpen(false); setReverseReason(''); invalidate();
        qc.invalidateQueries({ queryKey: ['retailer'] });
        // The Super Admin's queue just gained a row; their hub and badge must not lag behind it.
        qc.invalidateQueries({ queryKey: ['approvals'] });
      },
      onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Reverse failed'),
    });

  if (isLoading) return <Skeleton active paragraph={{ rows: 8 }} />;
  if (isError || !r) return <Result status="404" title="Return not found" />;

  const isRequested = r.status === 'REQUESTED';
  const canWithdraw = isRequested && String(r.requestedBy) === String(userId);
  // Only an approver, and only while the goods are still expected. Once they are RECEIVED the stock has
  // already posted and a cancellation cannot unwind it — that is a stock adjustment, not a cancellation.
  const canCallOff = canApprove && (r.status === 'APPROVED' || r.status === 'IN_TRANSIT');
  const isReceivedView = r.status === 'RECEIVED' || r.status === 'CREDITED';
  // Tax columns only when there IS tax — a zero-rated return should not carry two empty columns.
  const hasLineTax = (r.lines ?? []).some((l: any) => Number(l.cgstAmt ?? 0) + Number(l.sgstAmt ?? 0) + Number(l.igstAmt ?? 0) > 0);
  // A dead return (rejected / withdrawn) is not "awaiting the warehouse" — it is never going there,
  // so its unreached stages are left out rather than promised.
  const live = !['REJECTED', 'CANCELLED'].includes(r.status);
  // A decision that was made on this return and then sent back — see supersededDecision.
  const undone = live && !r.approvedBy ? supersededDecision(r.history ?? []) : null;
  const reason = REASON_META[r.reasonCode]
    ?? { label: label(r.reasonCode) || 'Not recorded', tone: 'neutral' as TagTone, fault: undefined as undefined | 'seller' | 'buyer' };
  // `proofUrls` is a JSON column and has historically arrived both as an array and as an encoded string.
  const proofs: string[] = Array.isArray(r.proofUrls) ? r.proofUrls.filter(Boolean) : toImageUrls(r.proofUrls);
  // An accountant's open ask to void this return's credit note — nothing has been unwound yet.
  const reversalReq = r.reversalRequest ?? null;

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <CopyValue value={r.returnNo} label="Return no">
            <span className="type-section-title text-content">{r.returnNo}</span>
          </CopyValue>
          <Tag color={STATUS_COLOR[r.status] ?? 'default'}>{label(r.status)}</Tag>
        </div>
        {/* Hero actions are a ROW, secondary before primary. */}
        <div className="flex flex-wrap items-center gap-2">
          {canApprove && isRequested && (
            <>
              <Button variant="danger" onClick={() => setRejectOpen(true)} loading={reject.isPending}>Reject</Button>
              <Button variant="primary" onClick={() => approve.mutate()} loading={approve.isPending}>Approve</Button>
            </>
          )}
          {/* Secondary, so it sits BEFORE the step actually expected next: calling a return off is the
              exception — the goods never came — not what the approver opened the record to do. */}
          {canCallOff && (
            <Button variant="soft-danger" onClick={() => setCallOffOpen(true)} loading={callOff.isPending}>Call off</Button>
          )}
          {canReceive && r.status === 'APPROVED' && (
            <Popconfirm
              title="Mark this return as in transit?"
              description="Confirm the goods are on their way back to the warehouse."
              okText="Mark in transit"
              onConfirm={() => markInTransit.mutate()}
            >
              <Button variant="primary" loading={markInTransit.isPending}>Mark in transit</Button>
            </Popconfirm>
          )}
          {canReceive && r.status === 'IN_TRANSIT' && (
            <Button variant="primary" onClick={() => setReceiveOpen(true)}>Receive goods</Button>
          )}
          {canCredit && r.status === 'RECEIVED' && (
            <Button variant="primary" onClick={() => setCreditOpen(true)}>Issue credit note</Button>
          )}
          {canReverse && r.status === 'CREDITED' && !reversalReq && (
            /* A request is not a destructive act, so it does not wear the destructive button. */
            <Button variant={canDecideReversal ? 'danger' : 'soft-danger'} onClick={() => setReverseOpen(true)} loading={reverse.isPending}>
              {canDecideReversal ? 'Reverse credit note' : 'Request reversal'}
            </Button>
          )}
          {canWithdraw && <Button variant="danger" onClick={() => withdraw.mutate()} loading={withdraw.isPending}>Withdraw</Button>}
        </div>
      </div>

      {/* AN ASK IN FLIGHT. Said at the top because it changes what every control below it means:
          the note is still live, the retailer still holds the credit, and nobody should raise a
          second request or wonder why the reverse button has gone. */}
      {reversalReq && (
        <div className="mb-3 rounded-card border border-warning-500/35 bg-warning-500/[0.09] px-3.5 py-3 shadow-e1">
          <div className="flex flex-wrap items-center gap-2">
            <Clock className="size-4 text-harvest-700 dark:text-harvest-300" />
            <span className="type-body-strong text-content">
              Reversal of {reversalReq.noteNos || 'this credit note'} is waiting on the Super Admin
            </span>
            {Number(reversalReq.amount) > 0 && <Tag tone="gold">{inr2(reversalReq.amount)} would be debited back</Tag>}
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 type-label text-muted">
            <span>Asked by</span>
            <span className="type-body-strong text-content">{reversalReq.requestedByName ?? 'someone'}</span>
            {roleLabel(reversalReq.requestedByRole ?? '') !== reversalReq.requestedByName && <RoleBadge role={reversalReq.requestedByRole} />}
            {reversalReq.requestedAt && <span>· {formatIst(reversalReq.requestedAt)}</span>}
          </div>
          {reversalReq.reason && <p className="mt-1.5 whitespace-pre-line break-words type-body text-content">“{reversalReq.reason}”</p>}
          <p className="mt-1.5 type-caption text-muted">
            Nothing has been unwound — the credit note is still live and the retailer keeps the credit until it is decided.
          </p>
          {canDecideReversal && (
            <div className="mt-2.5">
              <Button size="sm" variant="default"
                onClick={() => navigate(`/approvals?lane=credit_note_reversal_request${reversalReq.id ? `&focus=${reversalReq.id}` : ''}`)}>
                Decide it
              </Button>
            </div>
          )}
        </div>
      )}
      {isRequested && (
        <Alert
          type="info" className="mb-3"
          title="Awaiting approval. Once an admin approves, the warehouse receives + inspects the goods, then Accounts issues the credit note."
        />
      )}
      {canReceive && r.status === 'APPROVED' && (
        <Alert
          type="info" className="mb-3"
          title="Approved. Mark the goods in transit when the retailer ships them back, then receive + inspect them to restock."
        />
      )}
      {canReceive && r.status === 'IN_TRANSIT' && (
        <Alert
          type="warning" className="mb-3"
          title="Goods are in transit. Receive them to record what actually arrived and its condition — resalable units re-enter saleable stock."
        />
      )}
      {canCredit && r.status === 'RECEIVED' && (
        <Alert
          type="info" className="mb-3"
          title="Received + inspected. Issue the credit note — the credit posts to the retailer's advance (credit) balance; there is no cash refund."
        />
      )}
      {/* WHAT IT IS — the document and its parties on the left, why it came back on the right. One flat
          two-column grid of eleven hairline cells made "who" and "why" the same weight as "return date";
          they are not, and the reason is the half that decides what Accounts do with it. */}
      <div className="grid gap-3 lg:grid-cols-2">
        <Section title="Where it came from" icon={<FileText className="size-3.5" />}>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            <OField label="Retailer" className="col-span-2">
              <span className="flex min-w-0 flex-wrap items-center gap-2">
                <EntityAvatar name={r.companyName ?? String(r.retailerId ?? '?')} size={28} />
                <RetailerLink id={r.retailerId}>
                  <span className="type-body-strong">{r.companyName ?? `#${r.retailerId}`}</span>
                </RetailerLink>
                {r.retailerCode && (
                  <CopyValue value={r.retailerCode} label="Retailer code">
                    <CodeChip hue={entityHue(r.retailerCode)}>{r.retailerCode}</CodeChip>
                  </CopyValue>
                )}
              </span>
            </OField>
            <OField label="Order">
              {r.orderId ? <OrderLink id={r.orderId}>{r.orderNo ?? r.orderId}</OrderLink> : '—'}
              {r.orderStatus && <div className="mt-1"><Tag>{orderStatusLabel(r.orderStatus)}</Tag></div>}
            </OField>
            <OField label="Warehouse">
              {r.warehouseCode
                ? <span className="flex min-w-0 flex-wrap items-center gap-1.5">
                    <CodeChip hue={entityHue(r.warehouseCode)} className="shrink-0">{r.warehouseCode}</CodeChip>
                    <span className="min-w-0">{r.warehouseName}</span>
                  </span>
                : (r.warehouseName ?? (r.warehouseId ? `#${r.warehouseId}` : '—'))}
            </OField>
            {/* Order-level returns can span several invoices → no single "against invoice"; the credit
                note(s) attach per invoice at the Accounts step. Only a legacy single-invoice return
                carries one. */}
            {r.invoiceNo && (
              <OField label="Against invoice">
                <CopyValue value={r.invoiceNo} label="Invoice no"><CodeChip>{r.invoiceNo}</CodeChip></CopyValue>
              </OField>
            )}
            <OField label="Return date">
              {fmtDate(r.returnDate) ?? <span className="type-caption text-warning-600 dark:text-harvest-300">not recorded</span>}
            </OField>
          </div>
        </Section>

        <Section title="Why it came back" icon={<AlertTriangle className="size-3.5" />} className="flex flex-col">
          <div className="flex flex-wrap items-center gap-2">
            <Tag tone={reason.tone}>{reason.label}</Tag>
          </div>
          {/* One chip cannot speak for products that came back for different reasons. When they do, the
              headline says so and hands the reader to the rows that carry the real answer — the fault is
              what decides the credit, and Accounts must not read this chip as the whole story. */}
          {r.reasonsVary
            ? <div className="mt-1.5 type-caption text-warning-600 dark:text-harvest-300">
                Products came back for different reasons — see each one below.
              </div>
            : reason.fault && <div className="mt-1.5 type-caption text-muted">{FAULT_LINE[reason.fault]}</div>}
          {/* The retailer's own words. Free text WRAPS — a one-line clip with a tooltip hides the
              sentence that explains the claim. */}
          {r.reasonNote && (
            <p className="mt-2.5 whitespace-pre-line break-words type-body text-content">{r.reasonNote}</p>
          )}
          <div className="mt-3 border-t border-line pt-2.5 max-lg:mt-3 lg:mt-auto">
            <div className="type-label text-muted">Proof</div>
            <div className="mt-1">
              <AttachmentCell
                files={proofs}
                label="Return proof"
                size={44}
                maxThumbs={4}
                empty={<span className="type-caption text-warning-600 dark:text-harvest-300">nothing attached</span>}
              />
            </div>
          </div>
        </Section>
      </div>

      {/* WHO HANDLED IT — the named acts, in lifecycle order, each with the authority behind it. The
          audit trail at the foot still holds every transition; this is the decision trail, which is
          what a reader of a credited return is actually checking. */}
      <Section title="Who handled it" icon={<Users className="size-3.5" />} className="mt-3">
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <ActorCard act="raised" label="Raised by" name={r.requestedByName} role={r.requestedByRole}
            userId={r.requestedBy} at={r.createdAt ?? r.returnDate} />
          {/*
            * A DECISION THAT WAS TAKEN BACK IS STILL SOMETHING THAT HAPPENED.
            *
            * Leading with "awaiting a fresh decision" and demoting the decision itself to a grey line
            * underneath made one card contradict itself: the headline said nobody had acted, the small
            * print said two people had. The fact goes FIRST, in the same shape as every other card here
            * — act in the label, person as the value, role beside them — and the waiting becomes what
            * it actually is: the consequence, stated in the note.
            *
            * The label follows the act, so a rejection is never filed under "Approved by".
            */}
          <ActorCard
            act={undone ? 'requested' : 'approved'}
            label={undone ? `${undone.verb} by` : 'Approved by'}
            name={undone ? (undone.decision.changedByName ?? 'A colleague') : r.approvedByName}
            role={undone ? undone.decision.changedByRole : r.approvedByRole}
            userId={undone ? undone.decision.changedBy : r.approvedBy}
            at={undone ? undone.decision.createdAt : r.approvedAt}
            awaiting={live && !undone ? 'an approver' : undefined}
            note={undone ? (
              <>
                Sent back by{' '}
                <UserLink id={undone.undo.changedBy} role={undone.undo.changedByRole}>
                  {undone.undo.changedByName ?? 'a Super Admin'}
                </UserLink>{' '}
                {formatIst(undone.undo.createdAt)} — needs deciding again
              </>
            ) : undefined} />
          <ActorCard act="received" label="Received by" name={r.receivedByName} role={r.receivedByRole}
            userId={r.receivedBy} at={r.receivedAt} awaiting={live ? 'the warehouse' : undefined} />
          <ActorCard act="credited" label="Credited by" name={r.creditedByName} role={r.creditedByRole}
            userId={r.creditedBy} at={r.creditedAt} awaiting={live ? 'Accounts' : undefined} />
          {reversalReq && (
            <ActorCard act="requested" label="Reversal requested by" name={reversalReq.requestedByName} role={reversalReq.requestedByRole}
              userId={reversalReq.requestedBy} at={reversalReq.requestedAt} note={reversalReq.reason} />
          )}
          {(r.reversedByName || r.reversedBy) && (
            <ActorCard act="reversed" label="Credit reversed by" name={r.reversedByName} role={r.reversedByRole}
              userId={r.reversedBy} at={r.reversedAt} note={r.reversalReason} />
          )}
        </div>
      </Section>

      {/* What the return is WORTH.
       *
       * GST is deliberately NOT shown before the credit note exists, and that is not a gap in this
       * panel — it is where the tax is actually computed. A lifecycle return stores a PRE-TAX,
       * CD-net rate on its lines and never writes tax onto them; the real CGST/SGST/IGST is worked
       * out per invoice at the Accounts credit step and lives on the credit note. Showing
       * "Taxable ₹0 / Tax ₹0" on a REQUESTED return was therefore not a small number — it was a
       * wrong one, and it would have stayed ₹0 forever.
       *
       * So: before credit, one honest pre-tax figure. After credit, the note's real tax breakup.
       */}
      {(() => {
        const lines: any[] = r.lines ?? [];
        const notes: any[] = r.creditNotes ?? [];
        /*
         * A REVERSED NOTE IS NOT CREDIT THE RETAILER HOLDS.
         *
         * The band summed every note this return ever produced, CANCELLED ones included — so the
         * moment a credit was reversed it still read "Credited ₹538.20 · 1 credit note" for money the
         * compensating debit had already taken back off the ledger, and after a re-credit it read
         * "₹1,076.40 · 2 credit notes" on a retailer holding ₹538.20. It also contradicted the Credit
         * notes table three inches below, which has always struck a reversed amount through.
         *
         * Only LIVE notes are credit. The reversed ones are still counted — as reversals, named in
         * their own right — because a return whose credit was voided and not re-issued must not read
         * like one that was never credited at all.
         */
        const live: any[] = notes.filter((n) => n.status !== 'CANCELLED');
        const reversed: any[] = notes.filter((n) => n.status === 'CANCELLED');
        const sum = (arr: any[], k: string) => arr.reduce((t, x) => t + Number(x[k] ?? 0), 0);
        const claim = Number(r.totalAmount ?? 0) || sum(lines, 'lineTotal');
        const credited = sum(live, 'amount');
        // `strong` is the cell that answers the question the band is asked — what was settled before
        // credit, what was actually credited after — so it gets the brand tint, not just bolder text.
        const cell = (label: string, value: string, hint?: string, strong?: boolean) => (
          <div key={label} className={cn('px-3 py-2', strong ? 'bg-brand-500/[0.07]' : 'bg-surface')}>
            <div className="type-label text-subtle">{label}</div>
            <div className={cn('type-metric-sm', strong ? 'text-brand-700 dark:text-brand-300' : 'text-muted')}>{value}</div>
            {hint && <div className="type-caption text-subtle">{hint}</div>}
          </div>
        );
        if (!claim && !credited && !reversed.length) return null;
        const cells = live.length
          ? [
              cell('Taxable', inr2(sum(live, 'taxableAmt'))),
              cell('Tax', inr2(sum(live, 'cgstAmt') + sum(live, 'sgstAmt') + sum(live, 'igstAmt'))),
              cell('Claimed', inr2(claim), 'pre-tax'),
              cell('Credited', inr2(credited),
                `${live.length} credit note${live.length === 1 ? '' : 's'}${reversed.length ? ` · ${reversed.length} reversed` : ''}`, true),
            ]
          : [
              cell('Claim value', inr2(claim), 'pre-tax, at the billed rate', true),
              cell('Products', String(lines.length)),
              cell('Units requested', fmtQty(lines.reduce((t, l) => t + Number(l.requestedQty ?? 0), 0))),
              // A return sitting at RECEIVED because its credit was VOIDED is not the same as one that
              // was never credited, and the band is where that difference has to show.
              reversed.length
                ? cell('Credit reversed', inr2(sum(reversed, 'amount')),
                    `${reversed.length} note${reversed.length === 1 ? '' : 's'} voided — re-issue below`)
                : cell('Tax', '—', 'set when Accounts credit it'),
            ];
        return <div className="mt-3 grid grid-cols-2 gap-px overflow-hidden rounded-card border border-line bg-line shadow-e1 sm:grid-cols-4">{cells}</div>;
      })()}

      {/* WHAT CAME BACK — the receipt, added up.
       *
       * The per-product split below holds every sub-lot, but a reader asking "how much of this was damaged?"
       * should not have to expand rows and total them by eye. The quantities here PARTITION what was received:
       * they sum to it exactly, and each says in words where that stock went, because "Damaged" is a grade,
       * not a destination — the useful fact is that those units are out of sellable stock.
       */}
      {r.receivedSummary && (
        <>
          <div className="mt-4 mb-1.5 flex items-center gap-1.5 type-overline text-muted">
            <span className="text-brand-600 dark:text-brand-400"><PackageCheck className="size-3.5" /></span>What came back
          </div>
          <div className="rounded-card border border-line bg-surface p-3 shadow-e1">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <span className="type-metric text-content">{fmtQty(r.receivedSummary.receivedQty)}</span>
              <span className="type-body text-muted">
                units received{r.receivedSummary.saleableQty > 0 && <> · <span className="text-success-600 dark:text-success-400">{fmtQty(r.receivedSummary.saleableQty)} back in sellable stock</span></>}
              </span>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {r.receivedSummary.byCondition.map((c: any) => (
                <span key={c.condition} className="inline-flex items-baseline gap-1.5 rounded-control border border-line bg-surface-2/60 px-2 py-1">
                  <span className="type-body-strong tabular-nums text-content">{fmtQty(c.qty)}</span>
                  <Tag color={CONDITION_COLOR[c.condition] ?? 'default'}>{label(c.condition)}</Tag>
                  <span className="type-caption text-muted">{c.saleable ? 'back on sale' : 'quarantined'}</span>
                </span>
              ))}
            </div>
            {r.receivedSummary.shortQty > 0 && (
              <div className="mt-2 type-caption text-warning-600 dark:text-harvest-300">
                {fmtQty(r.receivedSummary.shortQty)} of the {fmtQty(r.receivedSummary.requestedQty)} units claimed never arrived — only what was received can be credited.
              </div>
            )}
          </div>
        </>
      )}

      <div className="mt-4 mb-1.5 flex items-center gap-1.5 type-overline text-muted">
        <span className="text-brand-600 dark:text-brand-400"><Package className="size-3.5" /></span>Products
      </div>
      <Table
        size="small"
        pagination={false}
        rowKey={(l: any) => String(l.id)}
        dataSource={r.lines ?? []}
        locale={{ emptyText: 'No products' }}
        // After receipt, expand a line to see its per-batch/per-condition split (each sub-lot the WHM recorded).
        expandable={isReceivedView ? {
          rowExpandable: (l: any) => (l.receipts?.length ?? 0) > 1,
          expandedRowRender: (l: any) => (
            <div className="pl-1">
              {(l.receipts ?? []).map((rc: any, i: number) => (
                <div key={i} className="flex items-center gap-2 py-0.5 text-xs">
                  <span className="w-16 text-right font-medium">{fmtQty(rc.qty)}</span>
                  <span className="w-32">{rc.receivedBatchNo || 'auto lot'}</span>
                  <Tag color={CONDITION_COLOR[rc.condition] ?? 'default'}>{label(rc.condition)}</Tag>
                </div>
              ))}
            </div>
          ),
        } : undefined}
        columns={[
          // The name carries a pack shot, an HSN and now the raiser's own words about this product, so it
          // needs a floor to sit on — without one the added columns squeezed it into a one-word-per-line wrap.
          { title: 'Product', dataIndex: 'productName', width: 260, render: (v: string, l: any) => (
            <div className="flex items-center gap-2.5">
              {/* The pack shot, openable. A return is an argument about physical goods — whoever
                  approves, receives or credits it is matching this row against a carton in front of
                  them, and the name alone does not settle which pack came back. No photo on file →
                  initials on the product's own packaging colour, the hue it wears in the stock tables. */}
              <ProductThumb name={v || `#${l.productId}`} srcs={productImages(l)} hue={accentForProductName(v)} size={34} />
              <div className="min-w-0">
                <div>{v ? <ProductLink name={v}>{v}</ProductLink> : `#${l.productId}`}</div>
                {l.hsnCode && <div className="type-caption text-subtle">HSN {l.hsnCode}</div>}
                {/* What the raiser said happened to THIS product. It describes the row, so it sits on the
                    row — free text wraps rather than being clipped to a tooltip nobody opens. */}
                {l.reasonNote && <div className="type-caption whitespace-pre-line wrap-break-word text-muted">{l.reasonNote}</div>}
              </div>
            </div>
          ) },
          { title: 'Requested qty', dataIndex: 'requestedQty', align: 'right', width: 124, render: (v: any, r: any) => {
            const phrase = boxLoosePhrase(r.boxQuantity, r.looseUnitQuantity, r.unitsPerBoxSnapshot);
            return phrase ? <span>{phrase}<div className="text-2xs text-subtle">= {fmtQty(v)} units</div></span> : fmtQty(v);
          } },
          // WHY THIS PRODUCT came back — shown only when the products disagree. When they all claim the same
          // thing, the panel above already says it once, and repeating it down every row is noise.
          ...(r.reasonsVary ? [
            { title: 'Reason', dataIndex: 'reasonCode', width: 150, render: (v: string) => {
              const m = REASON_META[String(v)] ?? { label: v ? label(v) : '—', tone: 'neutral' as TagTone };
              return v ? <Tag tone={m.tone}>{m.label}</Tag> : '—';
            } },
          ] : []),
          /*
           * THE LOT — one column, whichever half of the story is the live one.
           *
           * Before receipt the only lot that exists is the CLAIM, and the warehouse needs it to know what to
           * expect. After receipt the useful fact is what actually ARRIVED; the claim matters then only when
           * the two disagree, which is exactly what a "Batch mismatch" grade rests on. Carrying both as
           * separate columns squeezed the product name into a six-line wrap and pushed Condition off the
           * edge — so the claim moves under the received lot, and only when it contradicts it.
           */
          ...(!isReceivedView ? [
            { title: 'Lot claimed', dataIndex: 'claimedBatchNo', width: 190, render: (v: string, l: any) => {
              if (!v) return <span className="type-caption text-warning-600 dark:text-harvest-300">not stated</span>;
              return (
                <div className="flex min-w-0 flex-wrap gap-1">
                  <CopyValue value={v} label="Lot claimed"><CodeChip hue={entityHue(v)}>{v}</CodeChip></CopyValue>
                  {l.claimedBatchDispatched === false && (
                    <div className="mt-0.5 type-caption text-warning-600 dark:text-harvest-300">not a lot we dispatched</div>
                  )}
                </div>
              );
            } },
          ] : [
            { title: 'Received qty', dataIndex: 'receivedQty', align: 'right' as const, width: 100, render: (v: any) => fmtQty(v ?? 0) },
            // Lot numbers here run long ("Biovin 20kg-OPENING-377"); a chip does not wrap, so a narrow column
            // pushes it straight over the next one. Give it room and let the cell wrap rather than overflow.
            { title: 'Lot', dataIndex: 'receivedBatchNo', width: 190, render: (v: string, l: any) => (
              <div className="flex min-w-0 flex-wrap gap-1">
                {v
                  ? <CopyValue value={v} label="Lot received"><CodeChip hue={entityHue(v)}>{v}</CodeChip></CopyValue>
                  : <span className="text-muted">{(l.receipts?.length ?? 0) > 1 ? 'multiple' : '—'}</span>}
                {l.claimedBatchMatchesReceived === false && l.claimedBatchNo && (
                  <div className="mt-0.5 type-caption text-danger-600 dark:text-danger-400">≠ claimed {l.claimedBatchNo}</div>
                )}
              </div>
            ) },
            { title: 'Condition', dataIndex: 'condition', width: 122, render: (v: string) => v ? <Tag color={CONDITION_COLOR[v] ?? 'default'}>{label(v)}</Tag> : '—' },
          ]),
          // The money on the line. A return is a claim against the ledger, so the rate it was billed
          // at and what that comes to belong beside the quantity, not only in the credit note later.
          { title: 'Rate', dataIndex: 'unitPrice', align: 'right' as const, width: 96,
            render: (v: any) => (Number(v) ? inr2(v) : '—') },
          ...(hasLineTax ? [
            { title: 'Taxable', dataIndex: 'taxableAmt', align: 'right' as const, width: 120, render: (v: any) => (Number(v) ? inr2(v) : '—') },
            { title: 'Tax', align: 'right' as const, width: 110,
              render: (_: any, l: any) => {
                const t = Number(l.cgstAmt ?? 0) + Number(l.sgstAmt ?? 0) + Number(l.igstAmt ?? 0);
                return t ? inr2(t) : '—';
              } },
          ] : []),
          { title: 'Amount', dataIndex: 'lineTotal', align: 'right' as const, width: 108,
            render: (v: any) => (Number(v) ? <b>{inr2(v)}</b> : '—') },
        ]}
      />

      {(r.creditNotes?.length ?? 0) > 0 && (
        <>
          <div className="mt-4 mb-1.5 flex items-center gap-1.5 type-overline text-muted">
            <span className="text-brand-600 dark:text-brand-400"><Receipt className="size-3.5" /></span>Credit notes
          </div>
          <Table
            size="small"
            pagination={false}
            rowKey={(cn: any) => String(cn.noteNo)}
            dataSource={r.creditNotes ?? []}
            columns={[
              // The note number opens the canonical credit-note view (links + reversal + ledger trail).
              { title: 'Credit note', dataIndex: 'noteNo', render: (v: string, cn: any) => (
                <button className="font-medium text-brand-700 hover:underline dark:text-brand-400" onClick={() => setNoteDrawerId(String(cn.id))}>{v}</button>
              ) },
              { title: 'Against invoice', dataIndex: 'invoiceNo', render: (v: string) => v || '—' },
              { title: 'Status', dataIndex: 'status', width: 110, render: (v: string) => v === 'CANCELLED' ? <Pill color="red">Reversed</Pill> : <Pill color="green">Issued</Pill> },
              { title: 'Taxable', dataIndex: 'taxableAmt', align: 'right', width: 110, render: (v: any) => inr2(v) },
              { title: 'GST', align: 'right', width: 110, render: (_: any, cn: any) => inr2(Number(cn.cgstAmt) + Number(cn.sgstAmt) + Number(cn.igstAmt)) },
              // A reversed note's amount is struck through — it no longer contributes to the advance-in-credit.
              { title: 'Amount', dataIndex: 'amount', align: 'right', width: 120, render: (v: any, cn: any) => (
                <span className={cn.status === 'CANCELLED' ? 'text-subtle line-through' : 'font-semibold'}>{inr2(v)}</span>
              ) },
            ]}
          />
        </>
      )}

      <div className="mt-4 mb-2 flex items-center gap-1.5 type-overline text-muted">
        <span className="text-brand-600 dark:text-brand-400"><History className="size-3.5" /></span>Audit trail
      </div>
      <Timeline
        items={(r.history ?? []).map((h: any) => ({
          color: STATUS_COLOR[h.toStatus] ?? 'gray',
          children: (
            <div>
              <span className="font-medium">{h.fromStatus ? `${label(h.fromStatus)} → ` : ''}{label(h.toStatus)}</span>
              <span className="ml-2 text-xs text-subtle">
                {formatIst(h.createdAt)}
                {h.changedByName ? <> · <UserLink id={h.changedBy} role={h.changedByRole}>{h.changedByName}</UserLink></> : ''}
              </span>
              {h.changedByName && roleLabel(h.changedByRole ?? '') !== h.changedByName && (
                <RoleBadge role={h.changedByRole} className="ml-1.5 align-middle" />
              )}
              {h.notes ? <div className="text-xs text-muted">{h.notes}</div> : null}
            </div>
          ),
        }))}
      />

      <Modal
        open={rejectOpen}
        title="Reject this return"
        onOpenChange={(o) => { if (!o) setRejectOpen(false); }}
        footer={<>
          <Button variant="default" onClick={() => setRejectOpen(false)}>Cancel</Button>
          <Button variant="danger" loading={reject.isPending} disabled={!rejectReason.trim()} onClick={() => reject.mutate(rejectReason.trim())}>Reject</Button>
        </>}
      >
        <Input.TextArea rows={3} maxLength={255} placeholder="Reason for rejection (required — the requester will see this)" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
      </Modal>

      <Modal
        open={callOffOpen}
        title="Call off this return"
        onOpenChange={(o) => { if (!o) setCallOffOpen(false); }}
        footer={<>
          <Button variant="default" onClick={() => setCallOffOpen(false)}>Cancel</Button>
          <Button variant="danger" loading={callOff.isPending} disabled={!callOffReason.trim()} onClick={() => callOff.mutate(callOffReason.trim())}>Call off</Button>
        </>}
      >
        <div className="mb-2.5 rounded-control border border-sky-500/25 bg-sky-500/10 px-3.5 py-2.5 type-body text-content">
          This return was already approved, so the warehouse is expecting the goods. Calling it off tells them
          nothing is coming, raises no credit note, and releases the {fmtQty(r.lines?.reduce((s: number, l: any) => s + Number(l.requestedQty ?? 0), 0) ?? 0)} units
          so they can be returned again later.
        </div>
        <Input.TextArea
          rows={3} maxLength={255}
          placeholder="Why is it being called off? (required — the requester and the warehouse will see this)"
          value={callOffReason}
          onChange={(e) => setCallOffReason(e.target.value)}
        />
      </Modal>

      <ReceiveReturnModal
        open={receiveOpen}
        onClose={() => setReceiveOpen(false)}
        returnId={id}
        warehouseId={String(r.warehouseId ?? '')}
        lines={r.lines ?? []}
      />

      <CreditReturnModal open={creditOpen} onClose={() => setCreditOpen(false)} returnId={id} />

      <Modal
        open={reverseOpen}
        title={canDecideReversal ? 'Reverse credit note' : 'Request a credit-note reversal'}
        onOpenChange={(o) => { if (!o) setReverseOpen(false); }}
        footer={<>
          <Button variant="default" onClick={() => setReverseOpen(false)}>Cancel</Button>
          <Button variant={canDecideReversal ? 'danger' : 'primary'} loading={reverse.isPending} disabled={!reverseReason.trim()} onClick={() => reverse.mutate(reverseReason.trim())}>
            {canDecideReversal ? 'Reverse credit note' : 'Send to the Super Admin'}
          </Button>
        </>}
      >
        {canDecideReversal ? (
          <div className="mb-3 rounded-control border border-danger-600/25 bg-danger-600/10 px-3 py-2 text-xs text-content">
            This voids the credit note(s) issued for this return and posts a compensating debit — the retailer's
            advance-in-credit is <span className="font-semibold">removed</span> and the return returns to
            <span className="font-semibold"> Received</span> so a corrected credit note can be re-issued. No stock moves.
          </div>
        ) : (
          /* The requester's copy promises nothing the request cannot deliver: it is an ASK, and until
             it is decided the retailer keeps every rupee of the credit. */
          <div className="mb-3 rounded-control border border-warning-500/35 bg-warning-500/10 px-3 py-2 text-xs text-content">
            This <span className="font-semibold">asks the Super Admin</span> to void the credit note(s) on this return.
            Nothing is unwound while it waits — the note stays live, the retailer keeps the credit, and the corrected
            note cannot be issued until they decide. They see your reason and decide on it.
          </div>
        )}
        <Input.TextArea rows={3} maxLength={255}
          placeholder={canDecideReversal
            ? 'Reason for reversal (required — recorded on the audit trail)'
            : 'Why should this credit note be voided? (required — the Super Admin decides on this)'}
          value={reverseReason} onChange={(e) => setReverseReason(e.target.value)} />
      </Modal>

      <CreditNoteModal id={noteDrawerId} onClose={() => setNoteDrawerId(null)} />
    </div>
  );
}
